import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import { useAuthStore } from '@/auth/auth-store';
import { EMPTY_GUID } from '@/shared/lib/empty-guid';
import { findPatientParticipant } from '@/shared/lib/appointment-participants';
import { parseWallClock } from '@/scheduling/lib/device-availability';
import { formatLocalDatetimeForWire } from '@/shared/lib/date-format';
import { buildDeviceMoveRequest, isDeviceAppointment } from '@/scheduling/lib/device-edit-request';
import {
  buildEditRecurringPayload,
  type RecurringScope,
} from '@/scheduling/components/appointments/edit-recurring-scope-dialog';
import { classifyDeviceBookingFailure } from '@/scheduling/lib/device-booking-errors';
import type { PreLoadAppointmentRequest, PreLoadAppointmentResponse } from '@/types/scheduling-api';
import { useUpdateDeviceAppointment } from './use-update-device-appointment';

/**
 * Move a DEVICE booking to a new window through the device route — PROD-2526.
 *
 * The time-only gestures (drag-and-drop on the Devices grid, and the series
 * menu's Reschedule) had no device path: the drop called the provider's
 * `PUT /api/Appointment/Reschedule`, and the series helper refused a device
 * outright ("missing patient / provider / profile context"). Both now come here,
 * and here goes to `PUT /api/Appointment/Resource/{id}` like every other device
 * edit.
 *
 * Same two steps as `useSeriesReschedule` (the practitioner's): PreLoad the
 * full booking — the device route needs its product snapshot, invoice id and
 * services, which a calendar row does not carry — then send it back with only
 * the window changed (`buildDeviceMoveRequest`). The PreLoad is asked with the
 * EMPTY GUID for the provider, which is what `PreLoadAppointmentDetails` binds
 * for a device booking (PROD-2179), and under the same query key the popup and
 * the edit rail use, so a warm cache is reused.
 *
 * `scope` is the course answer: `this-only` moves this session alone; the other
 * two carry the new time to the course's later / upcoming sessions.
 */
export interface DeviceRescheduleSource {
  id: string;
  participant?: readonly { referenceType?: string | null; patient?: { id?: string | null } | null }[] | null;
  appointmentProfile?: { id?: string | null } | null;
}

export interface DeviceRescheduleResult {
  ok: boolean;
  /** What to tell the operator when it did not work. */
  message?: string;
}

function toLocal(iso: string): Date {
  return parseWallClock(formatLocalDatetimeForWire(iso)) ?? new Date(Number.NaN);
}

export function useDeviceReschedule() {
  const queryClient = useQueryClient();
  const update = useUpdateDeviceAppointment();
  const userId = useAuthStore((s) => s.user?.userId) ?? '';

  const fire = useCallback(
    async (
      src: DeviceRescheduleSource,
      newStartISO: string,
      newEndISO: string,
      scope: RecurringScope = 'this-only',
    ): Promise<DeviceRescheduleResult> => {
      const patientId = findPatientParticipant(src.participant ?? undefined)?.patient?.id;
      const profileId = src.appointmentProfile?.id;
      // Callers hand over mixed shapes: the drop router's `newStartISO` is a
      // local no-Z string while its `newEndISO` is `toISOString()` (UTC, `Z`).
      // Reading a `Z` string as a wall clock lands it N hours early — the
      // PROD-1752 trap, measured here as a 1-minute booking on dev — so both go
      // through `formatLocalDatetimeForWire` first, exactly as the practitioner
      // write paths do. It is idempotent on an already-local string.
      const start = toLocal(newStartISO);
      const end = toLocal(newEndISO);
      if (!patientId || !profileId || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        return { ok: false, message: "Couldn't reschedule — the booking is missing its patient or product." };
      }

      let preload: PreLoadAppointmentResponse;
      try {
        preload = await queryClient.fetchQuery<PreLoadAppointmentResponse>({
          queryKey: ['appointment-details', src.id, patientId, EMPTY_GUID, profileId],
          queryFn: ({ signal }) =>
            post<PreLoadAppointmentResponse>(
              '/api/v1/Practice/PreLoadAppointmentDetails',
              {
                appointmentId: src.id,
                patientId,
                providerId: EMPTY_GUID,
                billing: {},
                patient: {},
                appointmentProfileId: profileId,
              } as PreLoadAppointmentRequest,
              { signal },
            ),
        });
      } catch {
        return { ok: false, message: "Couldn't load this booking to reschedule it. Try again." };
      }
      const appt = preload?.appointment;
      if (!appt || !isDeviceAppointment(appt)) {
        return { ok: false, message: "Couldn't reschedule — this is not a device booking." };
      }

      const series =
        scope !== 'this-only' && appt.recurring?.id
          ? { editRecurring: buildEditRecurringPayload(scope, appt.start, ['start', 'end']) }
          : null;
      const body = buildDeviceMoveRequest(appt, start, end, userId, series);
      try {
        await update.mutateAsync({ appointmentId: src.id, body });
        return { ok: true };
      } catch (err) {
        const f = classifyDeviceBookingFailure(err);
        return {
          ok: false,
          message:
            f.code === 'Unknown' && f.status !== 400 ? "Couldn't reschedule — try again." : f.message,
        };
      }
    },
    [queryClient, update, userId],
  );

  return { fire, isPending: update.isPending };
}
